import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, MapPin, ShieldCheck, MessageCircle, ExternalLink, Calendar, History, MessagesSquare, ClipboardCheck, User as UserIcon, Loader2, Pencil, X, Save } from "lucide-react";
import { logger } from "../lib/logger";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const STATUS_PILL = {
  scheduled: "bg-primary/10 text-primary border-primary/20",
  completed: "bg-secondary/15 text-secondary border-secondary/30",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  no_show: "bg-accent/25 text-[#3D405B] border-accent/50",
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setData(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not load user");
      logger.warn("user detail load failed:", e?.message);
      if (e?.response?.status === 404) nav("/admin?tab=users", { replace: true });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId]);

  const u = data?.user;
  const counts = data?.counts || {};
  const auditAll = useMemo(() => {
    const all = [...(data?.audit_as_actor || []), ...(data?.audit_as_target || [])];
    // Dedupe by audit_id
    const seen = new Set();
    const out = [];
    for (const e of all) {
      if (!seen.has(e.audit_id)) { seen.add(e.audit_id); out.push(e); }
    }
    return out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 text-center text-[#7A7D96]" data-testid="admin-user-detail-loading">
          <Loader2 className="mx-auto animate-spin mb-3 text-primary" size={28} />
          Loading user…
        </main>
      </div>
    );
  }
  if (!u) return null;

  const initial = (u.name || "?")[0];
  const tabs = [
    { id: "overview", label: "Overview", icon: UserIcon },
    { id: "bookings", label: `Bookings (${counts.bookings_total || 0})`, icon: Calendar },
    { id: "audit", label: `Audit (${auditAll.length})`, icon: History },
    { id: "whatsapp", label: `WhatsApp (${(data?.whatsapp_log || []).length})`, icon: MessageCircle },
    ...(data?.application ? [{ id: "application", label: "Application", icon: ClipboardCheck }] : []),
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="admin-user-detail">
        <Link to="/admin?tab=users" className="inline-flex items-center gap-2 text-sm text-[#7A7D96] hover:text-primary mb-4" data-testid="admin-user-back">
          <ArrowLeft size={14} /> Back to People
        </Link>

        <div className="flex items-center justify-end mb-3">
          <button
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-white text-sm font-bold shadow-md hover:shadow-lg transition-all"
            data-testid="admin-user-edit-btn"
          >
            <Pencil size={13} /> Edit details
          </button>
        </div>

        {/* Header card */}
        <div className="brand-card p-6 sm:p-7 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            {u.picture ? (
              <img src={u.picture} className="w-24 h-24 rounded-3xl object-cover" alt="" />
            ) : (
              <div className="w-24 h-24 rounded-3xl bg-accent flex items-center justify-center text-3xl font-extrabold text-[#3D405B]">{initial}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-[#3D405B]">{u.name}</h1>
                <span className="pill bg-secondary/15 text-secondary capitalize">{u.role}</span>
                {u.active === false && <span className="pill bg-destructive/10 text-destructive">inactive</span>}
                {u.verification_status === "pending_verification" && <span className="pill bg-accent/25 text-[#3D405B]">pending review</span>}
                {u.verification_status === "rejected" && <span className="pill bg-destructive/10 text-destructive">rejected</span>}
                {u.whatsapp_opt_in && <span className="pill bg-primary/10 text-primary inline-flex items-center gap-1"><MessageCircle size={11} /> WhatsApp</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
                <a href={`mailto:${u.email}`} className="inline-flex items-center gap-2 text-[#3D405B] hover:text-primary"><Mail size={14} /> {u.email}</a>
                {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-2 text-[#3D405B] hover:text-primary"><Phone size={14} /> {u.phone}</a>}
                {u.city && <span className="inline-flex items-center gap-2 text-[#5F627D]"><MapPin size={14} /> {u.city}</span>}
                {u.age != null && <span className="text-[#5F627D]">Age {u.age}</span>}
                {u.professional_body && <span className="inline-flex items-center gap-2 text-[#5F627D]"><ShieldCheck size={14} /> {u.professional_body} · {u.registration_number || "?"}</span>}
                {u.years_experience != null && <span className="text-[#5F627D]">👩‍⚕️ {u.years_experience} years experience</span>}
                {u.office_address && <span className="inline-flex items-center gap-2 text-[#5F627D] sm:col-span-2"><MapPin size={14} className="text-secondary" /> {u.office_address}</span>}
                {u.languages?.length > 0 && <span className="text-[#5F627D] sm:col-span-2">🗣️ {u.languages.join(", ")}</span>}
                {u.specialties?.length > 0 && <span className="text-[#5F627D] sm:col-span-2">💛 {u.specialties.join(", ")}</span>}
              </div>
              {u.bio && <p className="text-sm text-[#3D405B] mt-4 leading-relaxed whitespace-pre-line">{u.bio}</p>}
              <div className="text-[10px] text-[#7A7D96] mt-3 flex flex-wrap gap-x-3 gap-y-1" data-testid="user-detail-meta">
                <span>{u.user_id}</span>
                <span>· joined {fmt(u.created_at)}</span>
                {u.last_login_at ? (
                  <span data-testid="user-detail-last-login">· last login <strong className="text-secondary">{fmt(u.last_login_at)}</strong></span>
                ) : (
                  <span data-testid="user-detail-last-login" className="text-amber-700 font-bold">· never logged in</span>
                )}
                {u.password_changed_at && <span>· password rotated {fmt(u.password_changed_at)}</span>}
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-[#3D405B]/5">
            <Stat label="Total bookings" value={counts.bookings_total} />
            <Stat label="Scheduled" value={counts.bookings_scheduled} tone="primary" />
            <Stat label="Completed" value={counts.bookings_completed} tone="secondary" />
            <Stat label="Cancelled" value={counts.bookings_cancelled} tone="destructive" />
            {u.role === "counsellor" && counts.case_notes_as_counsellor > 0 && (
              <Stat label="Case notes written" value={counts.case_notes_as_counsellor} />
            )}
            {counts.case_notes_as_client > 0 && (
              <Stat label="Case notes about them" value={counts.case_notes_as_client} />
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm transition-all ${tab === t.id ? "bg-primary text-white shadow-md" : "bg-white border border-[#3D405B]/15 text-[#3D405B] hover:bg-[#3D405B]/5"}`} data-testid={`user-detail-tab-${t.id}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab user={u} counts={counts} recent={{ bookings: (data.bookings || []).slice(0, 5), audit: auditAll.slice(0, 5), whatsapp: (data.whatsapp_log || []).slice(0, 5) }} />}
        {tab === "bookings" && <BookingsTab bookings={data.bookings || []} />}
        {tab === "audit" && <AuditTab entries={auditAll} />}
        {tab === "whatsapp" && <WhatsAppTab entries={data.whatsapp_log || []} />}
        {tab === "application" && data.application && <ApplicationTab app={data.application} />}
      </main>

      {editing && (
        <FullEditUserModal
          initial={u}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    primary: "text-primary",
    secondary: "text-secondary",
    destructive: "text-destructive",
  }[tone] || "text-[#3D405B]";
  return (
    <div>
      <div className={`text-2xl font-extrabold ${cls}`}>{value ?? 0}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#7A7D96] mt-0.5">{label}</div>
    </div>
  );
}

function OverviewTab({ user, counts, recent }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="user-detail-overview">
      <div className="brand-card p-6">
        <div className="overline mb-3">Recent bookings</div>
        {recent.bookings.length === 0 ? (
          <p className="text-sm text-[#7A7D96]">No bookings yet.</p>
        ) : (
          <div className="space-y-3">
            {recent.bookings.map((b) => <MiniBooking key={b.booking_id} b={b} />)}
          </div>
        )}
      </div>
      <div className="brand-card p-6">
        <div className="overline mb-3">Recent audit trail</div>
        {recent.audit.length === 0 ? (
          <p className="text-sm text-[#7A7D96]">No recorded actions.</p>
        ) : (
          <div className="space-y-2">
            {recent.audit.map((a) => (
              <div key={a.audit_id} className="text-xs text-[#3D405B] flex items-center justify-between gap-2 py-1">
                <span className="truncate">{a.action}</span>
                <span className="text-[#7A7D96] whitespace-nowrap">{fmt(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniBooking({ b }) {
  const other = b._other;
  return (
    <Link to={`/session/${b.booking_id}`} className="block brand-card p-3 hover:shadow-sm transition-all" data-testid={`user-detail-mini-booking-${b.booking_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[#7A7D96]">{fmt(b.scheduled_at)}</div>
          <div className="font-bold text-[#3D405B] truncate">
            {other?.name || <span className="text-[#7A7D96] italic">deleted user</span>}
            {other && <span className="text-[10px] text-[#7A7D96] ml-2 capitalize">({other.role})</span>}
          </div>
        </div>
        <span className={`pill border shrink-0 ${STATUS_PILL[b.status] || STATUS_PILL.scheduled}`}>{b.status}</span>
      </div>
    </Link>
  );
}

function BookingsTab({ bookings }) {
  if (!bookings.length) return <div className="brand-card p-10 text-center text-[#7A7D96]">No bookings for this user.</div>;
  return (
    <div className="space-y-3" data-testid="user-detail-bookings">
      {bookings.map((b) => (
        <div key={b.booking_id} className="brand-card p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-[#7A7D96]">{fmt(b.scheduled_at)}</span>
                <span className="pill bg-primary/10 text-primary capitalize">{(b.session_mode || "online").replace("_", " ")}</span>
                <span className={`pill border ${STATUS_PILL[b.status] || STATUS_PILL.scheduled}`}>{b.status}</span>
              </div>
              <div className="font-bold text-[#3D405B] mt-1">
                with {b._other?.name || <span className="italic text-[#7A7D96]">deleted user</span>}
                {b._other && (
                  <>
                    <span className="text-[10px] text-[#7A7D96] ml-2 capitalize">({b._other.role})</span>
                    {b._other.email && <span className="text-[11px] text-[#5F627D] ml-2">· {b._other.email}</span>}
                  </>
                )}
              </div>
              {b.topic && <p className="text-xs text-[#5F627D] mt-1"><strong className="text-[#3D405B]">Topic:</strong> {b.topic}</p>}
              {b.session_mode === "in_person" && b.office_address && <p className="text-xs text-[#5F627D] mt-1"><strong className="text-[#3D405B]">Office:</strong> {b.office_address}</p>}
            </div>
            <Link to={`/session/${b.booking_id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-[#3D405B]/15 text-[#3D405B] text-xs font-bold hover:bg-[#3D405B]/5">
              <ExternalLink size={12} /> Open
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditTab({ entries }) {
  if (!entries.length) return <div className="brand-card p-10 text-center text-[#7A7D96]">No audit entries.</div>;
  return (
    <div className="brand-card overflow-hidden" data-testid="user-detail-audit">
      {entries.map((e) => (
        <div key={e.audit_id} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_180px] gap-3 px-5 py-3 border-b border-[#3D405B]/5 text-sm last:border-b-0">
          <div className="text-xs text-[#7A7D96]">{fmt(e.created_at)}</div>
          <div>
            <div className="font-bold text-[#3D405B]">{e.action}</div>
            {e.changes && <div className="text-[11px] text-[#7A7D96] mt-0.5">changed: {Object.keys(e.changes).slice(0, 4).join(", ")}</div>}
          </div>
          <div className="text-xs text-[#5F627D] sm:text-right">
            {e.actor_email && <div className="truncate">{e.actor_email}</div>}
            {e.target_label && <div className="truncate">→ {e.target_label}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function WhatsAppTab({ entries }) {
  if (!entries.length) return <div className="brand-card p-10 text-center text-[#7A7D96]">No WhatsApp sends for this user.</div>;
  return (
    <div className="brand-card overflow-hidden" data-testid="user-detail-whatsapp">
      {entries.map((e) => (
        <div key={`${e.created_at}-${e.purpose}`} className="px-5 py-3 border-b border-[#3D405B]/5 text-sm last:border-b-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[#3D405B]">{e.purpose}</span>
                <span className={`pill ${e.status === "sent" ? "bg-secondary/15 text-secondary" : "bg-destructive/10 text-destructive"}`}>{e.status}</span>
              </div>
              {e.body_preview && <p className="text-xs text-[#5F627D] mt-1 line-clamp-2">{e.body_preview}</p>}
              {e.error_message && <p className="text-xs text-destructive mt-1">⚠ {e.error_message}</p>}
            </div>
            <div className="text-xs text-[#7A7D96] whitespace-nowrap">{fmt(e.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApplicationTab({ app }) {
  const [downloading, setDownloading] = useState(false);
  const downloadCv = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { data } = await api.get(`/admin/applications/${app.application_id}/cv`);
      // The endpoint returns { cv_document: <base64 with data-URI prefix or raw>, cv_filename }
      const raw = data.cv_document || "";
      const filename = data.cv_filename || "cv";
      // Handle both data-URI and pure base64
      const commaIdx = raw.indexOf(",");
      const base64 = commaIdx >= 0 ? raw.slice(commaIdx + 1) : raw;
      // Try to infer mime from the data-URI prefix or filename
      let mime = "application/octet-stream";
      const mimeMatch = raw.match(/^data:([^;]+);/);
      if (mimeMatch) mime = mimeMatch[1];
      else if (/\.pdf$/i.test(filename)) mime = "application/pdf";
      else if (/\.(png|jpe?g|webp|gif)$/i.test(filename)) mime = `image/${filename.split(".").pop().toLowerCase().replace("jpg", "jpeg")}`;
      // Decode + blob
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not download CV");
    } finally { setDownloading(false); }
  };

  return (
    <div className="brand-card p-6 space-y-3 text-sm" data-testid="user-detail-application">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`pill ${app.status === "approved" ? "bg-secondary/15 text-secondary" : app.status === "rejected" ? "bg-destructive/10 text-destructive" : "bg-accent/25 text-[#3D405B]"}`}>{app.status}</span>
        <span className="text-xs text-[#7A7D96]">Submitted {fmt(app.created_at)}</span>
        {app.reviewed_at && <span className="text-xs text-[#7A7D96]">· Reviewed {fmt(app.reviewed_at)}</span>}
        {app.cv_filename ? (
          <button
            onClick={downloadCv}
            disabled={downloading}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-white text-xs font-bold shadow-md hover:shadow-lg disabled:opacity-50"
            data-testid="user-detail-download-cv"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
            {downloading ? "Preparing…" : `Download CV · ${app.cv_filename}`}
          </button>
        ) : (
          <span className="ml-auto text-xs italic text-amber-700 font-bold" data-testid="user-detail-cv-missing">⚠ No CV on file</span>
        )}
      </div>
      <div><strong className="text-[#3D405B]">Body:</strong> {app.professional_body} · {app.registration_number}</div>
      <div><strong className="text-[#3D405B]">Practice mode:</strong> <span className="capitalize">{app.practice_mode?.replace("_", " ")}</span></div>
      {app.years_experience != null && <div><strong className="text-[#3D405B]">Years experience:</strong> {app.years_experience}</div>}
      {app.languages?.length > 0 && <div><strong className="text-[#3D405B]">Languages:</strong> {app.languages.join(", ")}</div>}
      {app.specialties?.length > 0 && <div><strong className="text-[#3D405B]">Specialties:</strong> {app.specialties.join(", ")}</div>}
      {app.bio && <div className="pt-2 border-t border-[#3D405B]/5"><strong className="text-[#3D405B]">Bio:</strong><p className="mt-1 text-[#5F627D] leading-relaxed whitespace-pre-line">{app.bio}</p></div>}
      {app.review_notes && <div className="pt-2 border-t border-[#3D405B]/5"><strong className="text-[#3D405B]">Review notes:</strong><p className="mt-1 text-[#5F627D]">{app.review_notes}</p></div>}
    </div>
  );
}


const SA_PROVINCES = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"];
const PRO_BODIES = ["HPCSA", "SACSSP", "ASCHP", "OTHER"];

function FullEditUserModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    name: initial.name || "",
    email: initial.email || "",
    role: initial.role || "client",
    phone: initial.phone || "",
    age: initial.age ?? "",
    bio: initial.bio || "",
    province: initial.province || "",
    city: initial.city || "",
    suburb: initial.suburb || "",
    office_address: initial.office_address || "",
    professional_body: initial.professional_body || "",
    registration_number: initial.registration_number || "",
    specialties: (initial.specialties || []).join(", "),
    languages: (initial.languages || []).join(", "),
    whatsapp_opt_in: !!initial.whatsapp_opt_in,
    active: initial.active !== false,
    verification_status: initial.verification_status || "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    const changes = {};
    const diff = (k, orig) => { const now = (f[k] ?? "").toString().trim(); const was = (orig ?? "").toString(); if (now !== was) changes[k] = now || null; };
    diff("name", initial.name); diff("email", initial.email); diff("phone", initial.phone);
    diff("bio", initial.bio); diff("province", initial.province); diff("city", initial.city);
    diff("suburb", initial.suburb); diff("office_address", initial.office_address);
    diff("professional_body", initial.professional_body); diff("registration_number", initial.registration_number);
    diff("verification_status", initial.verification_status);
    if (f.role !== initial.role) changes.role = f.role;
    if (f.whatsapp_opt_in !== !!initial.whatsapp_opt_in) changes.whatsapp_opt_in = f.whatsapp_opt_in;
    if (f.active !== (initial.active !== false)) changes.active = f.active;
    if (f.age !== "" && Number(f.age) !== (initial.age ?? "")) changes.age = Number(f.age);
    else if (f.age === "" && initial.age != null) changes.age = null;
    // Specialties / languages — trim + split
    const spOrig = (initial.specialties || []).join(", ");
    const spNow = f.specialties.trim();
    if (spNow !== spOrig) changes.specialties = spNow ? spNow.split(",").map((s) => s.trim()).filter(Boolean) : [];
    const lgOrig = (initial.languages || []).join(", ");
    const lgNow = f.languages.trim();
    if (lgNow !== lgOrig) changes.languages = lgNow ? lgNow.split(",").map((s) => s.trim()).filter(Boolean) : [];
    if (f.password) {
      if (f.password.length < 10) { toast.error("Password must be at least 10 characters"); return; }
      changes.password = f.password;
    }
    if (Object.keys(changes).length === 0) { toast.info("No changes to save"); return; }

    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${initial.user_id}`, changes);
      const rev = data._sessions_revoked || 0;
      toast.success(rev > 0 ? `Saved · signed the user out of ${rev} session${rev === 1 ? "" : "s"}` : "Saved");
      onSaved?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save";
      toast.error(typeof msg === "string" ? msg : "Could not save");
      logger.warn("admin user update failed:", err?.message);
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3.5 py-2 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none text-sm";
  const labelCls = "text-[10px] uppercase font-extrabold tracking-[0.18em] text-[#7A7D96] mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-[#3D405B]/40 overflow-y-auto py-10" data-testid="admin-full-edit-modal" onClick={onClose}>
      <div className="brand-card p-6 sm:p-7 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="overline mb-1">Full edit · admin</div>
            <h2 className="text-2xl font-extrabold text-[#3D405B]">{initial.name}</h2>
            <p className="text-xs text-[#7A7D96] mt-1">You have permission to change every field on this record.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#3D405B]/5" aria-label="Close" data-testid="admin-full-edit-close"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {/* Identity */}
          <section>
            <div className="text-xs font-extrabold uppercase tracking-wider text-secondary mb-2">Identity</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>Full name</label><input value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} required data-testid="fe-name" /></div>
              <div><label className={labelCls}>Email</label><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} required data-testid="fe-email" /></div>
              <div><label className={labelCls}>Role</label>
                <select value={f.role} onChange={(e) => set("role", e.target.value)} className={inputCls} data-testid="fe-role">
                  <option value="client">Client (youth)</option>
                  <option value="parent">Parent / caregiver</option>
                  <option value="counsellor">Counsellor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div><label className={labelCls}>Age</label><input type="number" min="0" max="120" value={f.age} onChange={(e) => set("age", e.target.value)} className={inputCls} placeholder="—" data-testid="fe-age" /></div>
            </div>
          </section>

          {/* Contact & geo */}
          <section>
            <div className="text-xs font-extrabold uppercase tracking-wider text-secondary mb-2">Contact &amp; location</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls}>Phone</label><input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+27821234567" className={inputCls} data-testid="fe-phone" /></div>
              <div className="flex items-center gap-2 pt-6">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#3D405B]">
                  <input type="checkbox" checked={f.whatsapp_opt_in} onChange={(e) => set("whatsapp_opt_in", e.target.checked)} className="w-4 h-4 accent-primary" data-testid="fe-whatsapp" />
                  WhatsApp opt-in
                </label>
              </div>
              <div><label className={labelCls}>Province</label>
                <select value={f.province} onChange={(e) => set("province", e.target.value)} className={inputCls} data-testid="fe-province">
                  <option value="">—</option>
                  {SA_PROVINCES.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </div>
              <div><label className={labelCls}>City</label><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="fe-city" /></div>
              <div><label className={labelCls}>Suburb</label><input value={f.suburb} onChange={(e) => set("suburb", e.target.value)} className={inputCls} data-testid="fe-suburb" /></div>
              <div><label className={labelCls}>Office address (counsellor only)</label><input value={f.office_address} onChange={(e) => set("office_address", e.target.value)} className={inputCls} placeholder="123 Main St, Sandton" data-testid="fe-office" /></div>
            </div>
          </section>

          {/* Counsellor pro details */}
          {(f.role === "counsellor" || initial.professional_body) && (
            <section>
              <div className="text-xs font-extrabold uppercase tracking-wider text-secondary mb-2">Counsellor credentials</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Professional body</label>
                  <select value={f.professional_body} onChange={(e) => set("professional_body", e.target.value)} className={inputCls} data-testid="fe-pro-body">
                    <option value="">—</option>
                    {PRO_BODIES.map((b) => (<option key={b} value={b}>{b}</option>))}
                  </select>
                </div>
                <div><label className={labelCls}>Registration number</label><input value={f.registration_number} onChange={(e) => set("registration_number", e.target.value)} className={inputCls} data-testid="fe-regno" /></div>
                <div><label className={labelCls}>Verification status</label>
                  <select value={f.verification_status} onChange={(e) => set("verification_status", e.target.value)} className={inputCls} data-testid="fe-verification">
                    <option value="">—</option>
                    <option value="pending_verification">pending verification</option>
                    <option value="verified">verified</option>
                    <option value="rejected">rejected</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#3D405B]">
                    <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-primary" data-testid="fe-active" />
                    Account active
                  </label>
                </div>
                <div className="sm:col-span-2"><label className={labelCls}>Specialties (comma-separated)</label><input value={f.specialties} onChange={(e) => set("specialties", e.target.value)} placeholder="Anxiety, Trauma, Adolescence" className={inputCls} data-testid="fe-specialties" /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Languages (comma-separated)</label><input value={f.languages} onChange={(e) => set("languages", e.target.value)} placeholder="English, Zulu, Afrikaans" className={inputCls} data-testid="fe-languages" /></div>
              </div>
            </section>
          )}

          {/* Bio */}
          <section>
            <label className={labelCls}>Bio</label>
            <textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={4} className={inputCls + " resize-y"} placeholder="A short intro shown to clients when browsing counsellors…" data-testid="fe-bio" />
          </section>

          {/* Password reset */}
          <section className="pt-3 border-t border-[#3D405B]/5">
            <label className={labelCls}>Reset password (optional — signs the user out everywhere)</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={f.password} onChange={(e) => set("password", e.target.value)} className={inputCls + " pr-20"} placeholder="Min 10 characters" autoComplete="new-password" data-testid="fe-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-[#7A7D96] hover:text-[#3D405B]">
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
          </section>

          <div className="flex gap-2 justify-end pt-4 border-t border-[#3D405B]/5">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#3D405B]/15 font-bold text-[#3D405B] hover:bg-[#3D405B]/5">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-white font-bold shadow-md hover:shadow-lg disabled:opacity-50" data-testid="fe-save-btn">
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

